<template>
  <div class="user-start-review">
    <CCard class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="page-header">
          <div>
            <div class="kicker">{{ text.kicker }}</div>
            <h2>{{ agreement ? (form.title || agreement.title) : text.title }}</h2>
            <p>{{ text.subtitle }}</p>
          </div>
        </div>

        <div v-if="error" class="notice notice--error">{{ error }}</div>
        <div v-if="success" class="notice notice--success">{{ success }}</div>

        <div v-if="agreement" class="detail-card">
          <div class="detail-row"><span>{{ text.fields.partner }}</span><strong>{{ form.partner || agreement.partner }}</strong></div>
          <div class="detail-row"><span>{{ text.fields.type }}</span><strong>{{ agreement.activityType }}</strong></div>
          <div class="detail-row"><span>{{ text.fields.status }}</span><strong>{{ agreement.derivedStageLabel }}</strong></div>
          <div class="detail-row"><span>{{ text.fields.currentVersion }}</span><strong>v{{ latestVersionNumber }}</strong></div>
        </div>

        <div class="panel-card panel-card--document-preview">
          <h3>{{ text.documentPreviewTitle }}</h3>
          <div class="panel-note">{{ text.documentPreviewCopy }}</div>
          <AgreementDocumentHeader
            v-if="agreement"
            class="panel-card__document-header"
            :title="form.title || agreement.title"
            :partner="form.partner || agreement.partner"
            :partner-logo="form.partnerLogo || agreement.partnerLogo"
            :agreement-type="agreement.activityType"
            :country="resolvedCountryText"
            title-label="Agreement Title"
            compact
            :show-meta="true"
          />
          <div v-if="uploadedDraftName" class="document-file-card">
            <div>
              <strong>{{ uploadedDraftName }}</strong>
              <div class="file-meta">{{ uploadedDraftMeta }}</div>
            </div>
          </div>
          <div v-if="displayDraftSections.length" class="document-preview">
            <div
              v-for="section in displayDraftSections"
              :key="section.key"
              class="preview-section"
              :class="{ 
                'preview-section--focus': focusedSectionKey === section.key,
                'preview-section--clause': section.isClause
              }"
            >
              <div v-if="sectionDisplayTitle(section)" class="preview-section__label">
                {{ sectionDisplayTitle(section) }}
                <span v-if="section.isClause" class="clause-badge">{{ section.category || 'Clause' }}</span>
              </div>
              <AgreementSignaturePreview
                v-if="isSignaturePreviewSection(section)"
                :section="section"
                left-header="Mae Fah Luang University"
                :right-header="[form.partner || (agreement && agreement.partner), resolvedCountryText].filter(Boolean).join('\n')"
              />
              <p v-else v-html="renderHighlightedSectionContent(section)"></p>
            </div>
          </div>
          <div v-else-if="uploadedDraftUrl" class="muted">
            {{ text.previewPending }}
            <a :href="uploadedDraftUrl" target="_blank" rel="noopener" class="document-file-card__link">{{ text.openFile }}</a>
          </div>
          <div v-else class="muted">{{ text.noUploadedPreview }}</div>
        </div>

        <div class="review-layout">
          <div class="review-main">
            <div class="panel-card">
              <div class="panel-card__header">
                <div>
                  <h3>{{ text.editDetailsTitle }}</h3>
                  <div class="panel-note">
                    {{ canEditAgreement ? text.editDetailsCopy : text.editLockedCopy }}
                  </div>
                </div>
                <span class="edit-state-pill" :class="{ 'edit-state-pill--locked': !canEditAgreement }">
                  {{ canEditAgreement ? text.editableNow : text.readOnlyNow }}
                </span>
              </div>
              <div v-if="!canEditAgreement" class="lock-banner">
                {{ text.lockedBanner }}
              </div>
              <div class="meta-form-grid">
                <label class="field">
                  <span>{{ text.fields.title }}</span>
                  <input v-model="form.title" type="text" :disabled="!canEditAgreement" @input="onDraftFieldChange">
                </label>
                <label class="field">
                  <span>{{ text.fields.partner }}</span>
                  <input v-model="form.partner" type="text" :disabled="!canEditAgreement" @input="onDraftFieldChange">
                </label>
                <label class="field">
                  <div class="field-heading field-heading--compact">
                    <span>{{ text.fields.country }}</span>
                  </div>
                  <div
                    ref="countryPicker"
                    class="country-picker"
                    :class="{ 'country-picker--disabled': !canEditAgreement }"
                    @focusin="openCountryPicker"
                    @focusout="handleCountryPickerBlur"
                  >
                    <div class="country-picker__control">
                      <span
                        v-for="country in selectedCountryValues"
                        :key="country"
                        class="country-chip"
                      >
                        {{ country }}
                        <button
                          v-if="canEditAgreement"
                          type="button"
                          class="country-chip__remove"
                          :aria-label="`Remove ${country}`"
                          @click.stop="removeCountry(country)"
                        >
                          ×
                        </button>
                      </span>
                      <input
                        v-if="canEditAgreement"
                        v-model="countrySearch"
                        type="search"
                        class="country-picker__input"
                        :placeholder="selectedCountryValues.length ? '' : text.selectCountries"
                        @keydown.enter.prevent="selectFirstFilteredCountry"
                        @keydown.backspace="removeLastCountryIfSearchEmpty"
                      >
                      <span v-else-if="!selectedCountryValues.length" class="country-picker__placeholder">
                        {{ text.selectCountry }}
                      </span>
                    </div>
                    <div
                      v-if="canEditAgreement && countryPickerOpen"
                      class="country-picker__menu"
                    >
                      <button
                        v-for="country in filteredCountryOptions"
                        :key="country"
                        type="button"
                        class="country-picker__option"
                        @mousedown.prevent="addCountry(country)"
                      >
                        {{ country }}
                      </button>
                      <div v-if="!filteredCountryOptions.length" class="country-picker__empty">
                        {{ text.noCountryResults }}
                      </div>
                    </div>
                  </div>
                </label>
                <label class="field">
                  <span>{{ text.fields.partnerEmail }}</span>
                  <input v-model="form.partnerEmail" type="email" :disabled="!canEditAgreement" @input="onDraftFieldChange">
                </label>
                <label class="field">
                  <span>{{ text.fields.contactPerson }}</span>
                  <input v-model="form.contactPerson" type="text" :disabled="!canEditAgreement" @input="onDraftFieldChange">
                </label>
                <label class="field">
                  <span>{{ text.fields.department }}</span>
                  <input v-model="form.department" type="text" :disabled="!canEditAgreement" @input="onDraftFieldChange">
                </label>
                <label class="field field--full">
                  <span>{{ text.fields.summary }}</span>
                  <textarea v-model="form.summary" rows="3" :disabled="!canEditAgreement" @input="onDraftFieldChange"></textarea>
                </label>
                <label class="field">
                  <span>{{ text.fields.partnerLogo }}</span>
                  <input
                    v-if="canEditAgreement"
                    type="file"
                    accept="image/png,image/jpeg"
                    @change="handlePartnerLogoUpload"
                  >
                  <small class="field-help">{{ text.partnerLogoFileTypes }}</small>
                  <div class="asset-card">
                    <div v-if="partnerLogoUrl" class="asset-card__preview">
                      <img :src="partnerLogoUrl" :alt="partnerLogoName" class="asset-card__image">
                    </div>
                    <div>
                      <strong>{{ partnerLogoName }}</strong>
                      <div class="file-meta">{{ text.partnerLogoHelp }}</div>
                    </div>
                  </div>
                </label>
              </div>
              <div v-if="canEditAgreement" class="actions actions--between">
                <span class="draft-state">{{ draftDirty ? text.unsavedChanges : text.allChangesSaved }}</span>
                <CButton color="secondary" variant="outline" :disabled="!draftDirty" @click="saveDraftChanges">
                  {{ text.saveChanges }}
                </CButton>
              </div>
            </div>

            <div class="panel-card">
              <h3>{{ text.editDraftTitle }}</h3>
              <div class="panel-note">{{ text.editDraftCopy }}</div>
              <div v-if="displayEditableSections.length" class="editable-sections">
                <div
                  v-for="(section, index) in displayEditableSections"
                  :id="editableSectionId(section)"
                  :key="section.key || index"
                  class="editable-section"
                  :class="editableSectionClass(section)"
                >
                  <div v-if="sectionChangeThreads(section).length" class="section-change-callout">
                    <div class="section-change-callout__head">
                      <span>{{ sectionChangeLabel(section) }}</span>
                      <strong>{{ text.requestedChangeInstruction }}</strong>
                    </div>
                    <button
                      v-for="thread in sectionChangeThreads(section)"
                      :key="thread.threadId"
                      type="button"
                      class="section-change-callout__item"
                      :class="{ 'section-change-callout__item--resolved': thread.status === 'resolved' }"
                      @click.stop="openThreadContext(thread)"
                    >
                      {{ thread.root.copy || thread.root.content }}
                    </button>
                  </div>
                  <label class="field">
                    <span>{{ text.fields.sectionTitle }}</span>
                    <input v-model="section.title" type="text" :disabled="!canEditAgreement" @input="onDraftFieldChange">
                  </label>
                  <label class="field">
                    <span>{{ text.fields.sectionContent }}</span>
                    <div
                      v-if="sectionHasAnchoredChanges(section)"
                      class="editable-section__anchored-preview"
                      v-html="renderHighlightedSectionContent(section)"
                    ></div>
                    <textarea v-model="section.content" rows="6" :disabled="!canEditAgreement" @input="onDraftFieldChange"></textarea>
                  </label>
                </div>
              </div>
              <div v-else class="muted">{{ text.noEditableSections }}</div>
            </div>
          </div>

          <div class="review-side">
            <div class="panel-card panel-card--compact">
              <h3>{{ text.versionHistory }}</h3>
              <div v-if="versions.length" class="version-list version-list--compact">
                <div v-for="version in versions.slice(0, 3)" :key="version.versionNumber" class="version-item version-item--compact">
                  <strong>v{{ version.versionNumber }}</strong>
                  <span>{{ formatCommentTime(version.createdAt) }}</span>
                </div>
              </div>
              <div v-else class="muted">{{ text.noVersions }}</div>
            </div>

            <div class="panel-card">
              <h3>{{ text.commentsTitle }}</h3>
              <div class="panel-note">{{ text.commentsCopy }}</div>
              <div v-if="commentThreads.length">
                <div v-for="thread in commentThreads" :key="thread.threadId" class="comment-thread">
                  <div
                    class="comment-card comment-card--interactive"
                    :class="{
                      'comment-card--active': replyTargetThreadId === thread.threadId,
                      'comment-card--resolved': thread.status === 'resolved',
                      'comment-card--unresolved': thread.status !== 'resolved'
                    }"
                    role="button"
                    tabindex="0"
                    @click="openThreadContext(thread)"
                    @keydown.enter.prevent="openThreadContext(thread)"
                    @keydown.space.prevent="openThreadContext(thread)"
                  >
                    <div class="comment-head">
                      <strong>{{ thread.root.author }}</strong>
                      <span>{{ thread.root.articleTitle || thread.root.section || 'Comment' }}</span>
                    </div>
                    <div class="comment-meta">{{ thread.root.roleLabel || thread.root.role }} · Page {{ thread.root.page || '-' }} · {{ formatCommentTime(thread.root.time) }} · v{{ thread.root.versionNumber || 1 }}</div>
                    <div
                      v-if="isCommitteeThread(thread)"
                      class="comment-change-target"
                      :class="{ 'comment-change-target--resolved': thread.status === 'resolved' }"
                    >
                      <span>{{ thread.status === 'resolved' ? text.changeResolved : text.changeRequested }}</span>
                      <strong>{{ commentTargetTitle(thread.root) }}</strong>
                      <em v-if="commentAnchorText(thread.root)">{{ commentAnchorText(thread.root) }}</em>
                    </div>
                    <div
                      v-if="shouldShowThreadStatusBadge(thread.status)"
                      class="thread-state-pill"
                      :class="threadStatusClass(thread.status)"
                    >
                      {{ threadStatusLabel(thread.status) }}
                    </div>
                    <p>{{ thread.root.copy }}</p>
                    <div class="thread-actions">
                      <button
                        v-if="canEditAgreement && thread.status !== 'resolved'"
                        type="button"
                        class="thread-link"
                        @click.stop="prepareReply(thread, thread.root)"
                      >
                        {{ text.reply }}
                      </button>
                    </div>
                  </div>
                  <div v-if="replyTargetThreadId === thread.threadId" class="inline-reply-indicator">
                    <span class="inline-reply-indicator__arrow">↳</span>
                    <span>{{ text.replyingIndicator }}</span>
                    <button type="button" class="thread-link" @click="clearReplyTarget">{{ text.clear }}</button>
                  </div>
                  <div v-if="replyTargetThreadId === thread.threadId" class="form-stack form-stack--composer form-stack--inline">
                    <div class="panel-title panel-title--composer">{{ text.replyToComment }}</div>
                    <label class="decision-label">
                      {{ text.fields.article }}
                      <select v-model="commentForm.articleKey" class="decision-select" @change="applyArticleSelection">
                        <option v-for="item in articleOptions" :key="item.key" :value="item.key">{{ item.title }}</option>
                      </select>
                    </label>
                    <div class="article-reference">
                      {{ text.commentingOn }} {{ currentArticleTitle() }}
                    </div>
                    <label class="decision-label" for="user-comment-box">
                      {{ text.fields.yourComment }}
                    </label>
                    <textarea
                      id="user-comment-box"
                      v-model="commentForm.content"
                      class="decision-note"
                      rows="6"
                      :disabled="!canEditAgreement"
                      :placeholder="text.commentPlaceholder"
                    />
                    <div class="decision-actions">
                      <CButton color="primary" :disabled="!canEditAgreement" @click="saveResponseComment">{{ text.postComment }}</CButton>
                      <CButton color="secondary" variant="outline" @click="closeUserComposer">{{ text.cancel }}</CButton>
                    </div>
                  </div>
                  <div v-if="thread.replies.length" class="thread-replies">
                    <div v-for="reply in thread.replies" :key="reply.commentId || reply.id" class="comment-card comment-card--reply">
                      <div class="comment-head">
                        <strong>{{ reply.author }}</strong>
                        <span>{{ reply.roleLabel || reply.role }}</span>
                      </div>
                      <div class="comment-meta">Page {{ reply.page || '-' }} · {{ formatCommentTime(reply.time) }} · v{{ reply.versionNumber || 1 }}</div>
                      <p>{{ reply.copy }}</p>
                      <div class="thread-actions thread-actions--reply">
                        <button
                          v-if="canEditAgreement && thread.status !== 'resolved'"
                          type="button"
                          class="thread-link"
                          @click.stop="prepareReply(thread, reply)"
                        >
                          {{ text.reply }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="muted">{{ text.noSuggestions }}</div>
            </div>

            <div v-if="canEditAgreement && !showUserComposer && !replyTargetThreadId" class="composer-launch">
              <button type="button" class="composer-launch__btn" @click="openUserComposer">
                {{ text.addNewComment }}
              </button>
            </div>

            <div v-if="canEditAgreement && showUserComposer && !replyTargetThreadId" class="form-stack form-stack--composer">
              <div class="panel-title panel-title--composer">{{ text.addUserComment }}</div>
              <label class="decision-label">
                {{ text.fields.article }}
                <select v-model="commentForm.articleKey" class="decision-select" @change="applyArticleSelection">
                  <option v-for="item in articleOptions" :key="item.key" :value="item.key">{{ item.title }}</option>
                </select>
              </label>
              <div class="article-reference">
                {{ text.commentingOn }} {{ currentArticleTitle() }}
              </div>
              <label class="decision-label" for="user-comment-box">
                {{ text.fields.yourComment }}
              </label>
              <textarea
                id="user-comment-box"
                v-model="commentForm.content"
                class="decision-note"
                rows="8"
                :disabled="!canEditAgreement"
                :placeholder="text.commentPlaceholder"
              />
              <div class="decision-actions">
                <CButton color="primary" :disabled="!canEditAgreement" @click="saveResponseComment">{{ text.postComment }}</CButton>
                <CButton color="secondary" variant="outline" @click="closeUserComposer">{{ text.cancel }}</CButton>
              </div>
            </div>

            <div class="panel-card">
              <h3>{{ text.submitRevisionTitle }}</h3>
              <div class="panel-note">{{ canEditAgreement ? text.submitRevisionCopy : text.submitRevisionLockedCopy }}</div>
              <div class="submit-summary">
                <span>{{ text.repliedCount(repliedThreadCount) }}</span>
                <span>{{ text.waitingCount(waitingReplyThreadCount) }}</span>
                <span>{{ text.resolvedCount(resolvedThreadCount) }}</span>
              </div>
              <div v-if="!canSubmitRevision" class="submit-warning">
                {{ submitWarning }}
              </div>
              <div class="actions">
                <CButton color="primary" :disabled="!canSubmitRevision" @click="resubmit">{{ text.submitRevisedDraft }}</CButton>
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import Service from '@/service/api'
import { canAutoRefresh } from '@/projects/utils/refresh-guard'
import {
  getAgreementSectionDisplayTitle,
  isSignatureAgreementSection
} from '@/utils/agreement-preview-sections'
import { COUNTRY_OPTIONS } from '@/utils/countries'
import { createWorkflowNotification } from '@/utils/workflow-notifications'
import AgreementDocumentHeader from '@/views/shared/AgreementDocumentHeader'
import AgreementSignaturePreview from '@/views/shared/AgreementSignaturePreview'

const CLAUSE_ARTICLE_KEY_PREFIX = 'selected-clause-article-'

function parseClauseArticleId(key) {
  const value = String(key || '').trim()
  return value.startsWith(CLAUSE_ARTICLE_KEY_PREFIX) ? value.slice(CLAUSE_ARTICLE_KEY_PREFIX.length) : ''
}

const PAGE_TEXT = {
  en: {
    kicker: 'User Review',
    title: 'Agreement Review',
    subtitle: 'Follow the live committee conversation, reply where needed, update the draft, and send back a stronger revision with confidence.',
    fields: {
      title: 'Agreement Title',
      partner: 'Partner',
      country: 'Country',
      partnerEmail: 'Partner Email',
      contactPerson: 'Contact Person',
      department: 'Department',
      summary: 'Additional Note',
      partnerLogo: 'Partner Logo',
      type: 'Type',
      status: 'Status',
      currentVersion: 'Current Version',
      sectionTitle: 'Section Title',
      sectionContent: 'Section Content',
      article: 'Article',
      yourComment: 'Your comment'
    },
    documentPreviewTitle: 'Full Uploaded Document Preview',
    documentPreviewCopy: 'Read the full contract inside the website first, then edit the same sections below for resubmission.',
    previewPending: 'The uploaded file exists, but the editable preview text is still being prepared. Image-based or scanned DOCX/PDF files can take longer because OCR needs to run first.',
    openFile: 'Open file',
    noUploadedPreview: 'No uploaded draft preview is available yet.',
    editDetailsTitle: 'Edit Agreement Details',
    editDetailsCopy: 'You can update the agreement title, partner details, logo box, and draft text while committee review is still in progress.',
    editLockedCopy: 'Editing is locked while this agreement is waiting for the admin final decision.',
    editableNow: 'Editable Now',
    readOnlyNow: 'Read Only',
    lockedBanner: 'This agreement has already been sent to admin, so user editing is paused. If admin sends it back for revision, editing will open again.',
    selectCountry: 'Select a country',
    selectCountries: 'Select one or more countries',
    searchCountries: 'Search countries',
    noCountryResults: 'No countries found',
    partnerLogoHelp: 'Upload a new partner logo to update the right-side logo box.',
    partnerLogoFileTypes: 'Accepted files: PNG, JPG, or JPEG.',
    logoFileTypeError: 'Partner logo must be a PNG, JPG, or JPEG file.',
    selectedCountriesBesideLabel: 'Selected countries are shown beside the Country label.',
    noCountrySelected: 'No country selected.',
    noPartnerLogo: 'No partner logo uploaded',
    editDraftTitle: 'Edit Current Draft',
    editDraftCopy: 'Update the working agreement text here before sending the next revision. Each resubmission saves a new version snapshot.',
    noEditableSections: 'No editable draft sections were loaded yet. Save an imported contract once to convert it into editable content. If the uploaded file is image-based or scanned, the OCR fallback may still be processing or may need a retry.',
    versionHistory: 'Version History',
    noVersions: 'No saved versions yet.',
    commentsTitle: 'Read Committee Comments',
    commentsCopy: 'Start here. Read what the committee asked for before you decide whether to reply, edit the draft, or both.',
    changeRequested: 'Change requested',
    changeResolved: 'Change resolved',
    needsChange: 'Needs change',
    requestedChangeInstruction: 'Committee marked this section for revision.',
    reply: 'Reply',
    replyingIndicator: 'Replying to this comment below.',
    clear: 'Clear',
    replyToComment: 'Reply To Comment',
    commentingOn: 'Commenting on:',
    commentPlaceholder: 'Reply to the committee, explain changes, or add a follow-up comment.',
    postComment: 'Post Comment',
    cancel: 'Cancel',
    noSuggestions: 'No committee suggestions yet.',
    addNewComment: 'Add New Comment',
    addUserComment: 'Add User Comment',
    saveChanges: 'Save Changes',
    unsavedChanges: 'Unsaved changes',
    allChangesSaved: 'All changes saved',
    saveSuccess: 'Agreement changes saved.',
    submitRevisionTitle: 'Submit Revised Draft',
    submitRevisionCopy: 'Review the admin or committee feedback, update the draft, then send the revised version back for another check.',
    submitRevisionLockedCopy: 'This agreement is read-only while admin reviews the final submission.',
    repliedCount: (count) => `${count} replied`,
    waitingCount: (count) => `${count} waiting for your reply`,
    resolvedCount: (count) => `${count} resolved`,
    submitRevisedDraft: 'Submit Revised Draft',
    submitWarningLocked: 'Editing is locked while admin reviews the final submission.',
    submitWarningNoComments: 'Wait for admin or committee feedback before submitting a revised draft.',
    submitWarningReplyAll: 'Reply to each committee comment thread before submitting the revised draft.',
    general: 'General',
    articleLabel: 'Article',
    draftDocument: 'Draft Document',
    uploadedDraftMeta: 'Word contract uploaded by the user',
    statusClarify: 'Clarify',
    statusResolved: 'Resolved',
    notification: {
      commentTitle: 'User added a new review comment',
      commentMessage: (title) => `${title || 'An agreement'} has a new user reply in the live review conversation.`,
      reviewSource: 'Committee Review',
      openReview: 'Open review',
      commentNextStep: 'Continue the committee discussion and review the new user comment.',
      resubmitTitle: 'User sent a revised draft back to committee',
      resubmitMessage: (title) => `${title || 'An agreement'} was revised and is ready for another committee review round.`,
      resubmitNextStep: 'Check the revised draft and continue the committee review.',
      responseNote: 'Updated draft resubmitted to committee for another review round.'
    }
  },
  th: {
    kicker: 'รีวิวของผู้ใช้',
    title: 'รีวิวข้อตกลง',
    subtitle: 'ติดตามบทสนทนาแบบสดกับคณะกรรมการ ตอบกลับเมื่อจำเป็น ปรับแก้ร่าง และส่งฉบับแก้ไขที่ดีขึ้นกลับไปอย่างมั่นใจ',
    fields: {
      title: 'ชื่อข้อตกลง',
      partner: 'คู่สัญญา',
      country: 'ประเทศ',
      partnerEmail: 'อีเมลคู่สัญญา',
      contactPerson: 'ผู้ประสานงาน',
      department: 'หน่วยงาน',
      summary: 'หมายเหตุเพิ่มเติม',
      partnerLogo: 'โลโก้คู่สัญญา',
      type: 'ประเภท',
      status: 'สถานะ',
      currentVersion: 'เวอร์ชันปัจจุบัน',
      sectionTitle: 'ชื่อส่วน',
      sectionContent: 'เนื้อหาส่วน',
      article: 'มาตรา',
      yourComment: 'ความคิดเห็นของคุณ'
    },
    documentPreviewTitle: 'ตัวอย่างเอกสารฉบับเต็มที่อัปโหลด',
    documentPreviewCopy: 'อ่านสัญญาฉบับเต็มในเว็บไซต์ก่อน แล้วค่อยแก้ไขส่วนเดียวกันด้านล่างเพื่อส่งฉบับแก้ไข',
    previewPending: 'มีไฟล์ที่อัปโหลดแล้ว แต่เว็บไซต์ยังไม่สามารถสร้างข้อความตัวอย่างที่แก้ไขได้ ซึ่งมักเกิดขึ้นเมื่อไฟล์ DOCX หรือ PDF เป็นภาพสแกนหรือเอกสารแบบรูปภาพ',
    openFile: 'เปิดไฟล์',
    noUploadedPreview: 'ยังไม่มีตัวอย่างร่างเอกสารที่อัปโหลด',
    editDetailsTitle: 'แก้ไขรายละเอียดข้อตกลง',
    editDetailsCopy: 'ระหว่างที่คณะกรรมการกำลังรีวิว คุณสามารถแก้ไขชื่อข้อตกลง รายละเอียดคู่สัญญา กล่องโลโก้ และข้อความในร่างได้',
    editLockedCopy: 'เมื่อส่งให้แอดมินตัดสินใจขั้นสุดท้ายแล้ว ระบบจะล็อกการแก้ไขชั่วคราว',
    editableNow: 'แก้ไขได้',
    readOnlyNow: 'อ่านอย่างเดียว',
    lockedBanner: 'ข้อตกลงนี้ถูกส่งให้แอดมินแล้ว จึงหยุดการแก้ไขของผู้ใช้ชั่วคราว หากแอดมินส่งกลับมาให้แก้ไขอีกครั้ง ระบบจะเปิดให้แก้ไขได้ใหม่',
    selectCountry: 'เลือกประเทศ',
    selectCountries: 'เลือกประเทศอย่างน้อยหนึ่งประเทศ',
    searchCountries: 'ค้นหาประเทศ',
    noCountryResults: 'ไม่พบประเทศ',
    partnerLogoHelp: 'อัปโหลดโลโก้คู่สัญญาใหม่เพื่ออัปเดตกล่องโลโก้ด้านขวา',
    partnerLogoFileTypes: 'ไฟล์ที่รองรับ: PNG, JPG หรือ JPEG',
    logoFileTypeError: 'โลโก้คู่สัญญาต้องเป็นไฟล์ PNG, JPG หรือ JPEG',
    selectedCountriesBesideLabel: 'ประเทศที่เลือกจะแสดงข้างป้ายประเทศ',
    noCountrySelected: 'ยังไม่ได้เลือกประเทศ',
    noPartnerLogo: 'ยังไม่มีโลโก้คู่สัญญา',
    editDraftTitle: 'แก้ไขร่างปัจจุบัน',
    editDraftCopy: 'อัปเดตข้อความของร่างข้อตกลงที่นี่ก่อนส่งฉบับแก้ไขครั้งถัดไป ทุกการส่งซ้ำจะบันทึก snapshot เวอร์ชันใหม่',
    noEditableSections: 'ยังไม่มีการโหลดส่วนของร่างที่แก้ไขได้ บันทึกสัญญาที่นำเข้าอย่างน้อยหนึ่งครั้งเพื่อแปลงเป็นเนื้อหาที่แก้ไขได้ หากไฟล์ที่อัปโหลดเป็นภาพสแกนหรือเอกสารแบบรูปภาพ อาจไม่มีข้อความที่แก้ไขได้',
    versionHistory: 'ประวัติเวอร์ชัน',
    noVersions: 'ยังไม่มีเวอร์ชันที่บันทึกไว้',
    commentsTitle: 'อ่านความคิดเห็นของคณะกรรมการ',
    commentsCopy: 'เริ่มจากตรงนี้ อ่านสิ่งที่คณะกรรมการขอแก้ไขก่อนตัดสินใจว่าจะตอบกลับ แก้ร่าง หรือทำทั้งสองอย่าง',
    changeRequested: 'ขอให้แก้ไข',
    changeResolved: 'แก้ไขแล้ว',
    needsChange: 'ต้องแก้ไข',
    requestedChangeInstruction: 'คณะกรรมการระบุว่าส่วนนี้ต้องแก้ไข',
    reply: 'ตอบกลับ',
    replyingIndicator: 'กำลังตอบกลับความคิดเห็นนี้ด้านล่าง',
    clear: 'ล้าง',
    replyToComment: 'ตอบกลับความคิดเห็น',
    commentingOn: 'กำลังแสดงความคิดเห็นที่:',
    commentPlaceholder: 'ตอบกลับคณะกรรมการ อธิบายการแก้ไข หรือเพิ่มความคิดเห็นติดตามผล',
    postComment: 'ส่งความคิดเห็น',
    cancel: 'ยกเลิก',
    noSuggestions: 'ยังไม่มีข้อเสนอแนะจากคณะกรรมการ',
    addNewComment: 'เพิ่มความคิดเห็นใหม่',
    addUserComment: 'เพิ่มความคิดเห็นของผู้ใช้',
    saveChanges: 'บันทึกการแก้ไข',
    unsavedChanges: 'มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก',
    allChangesSaved: 'บันทึกการเปลี่ยนแปลงแล้ว',
    saveSuccess: 'บันทึกการแก้ไขข้อตกลงแล้ว',
    submitRevisionTitle: 'ส่งร่างฉบับแก้ไข',
    submitRevisionCopy: 'ตรวจสอบข้อเสนอแนะจากผู้ดูแลหรือคณะกรรมการ ปรับแก้ร่าง แล้วส่งเวอร์ชันแก้ไขกลับไปให้ตรวจอีกครั้ง',
    submitRevisionLockedCopy: 'ข้อตกลงนี้เปิดอ่านได้อย่างเดียวระหว่างที่แอดมินกำลังพิจารณาฉบับสุดท้าย',
    repliedCount: (count) => `ตอบกลับแล้ว ${count}`,
    waitingCount: (count) => `รอตอบกลับ ${count}`,
    resolvedCount: (count) => `ปิดแล้ว ${count}`,
    submitRevisedDraft: 'ส่งร่างฉบับแก้ไข',
    submitWarningLocked: 'ระบบล็อกการแก้ไขไว้ชั่วคราวระหว่างที่แอดมินกำลังพิจารณาฉบับสุดท้าย',
    submitWarningNoComments: 'โปรดรอข้อเสนอแนะจากผู้ดูแลหรือคณะกรรมการก่อนส่งร่างฉบับแก้ไข',
    submitWarningReplyAll: 'ตอบกลับทุกเธรดความคิดเห็นของคณะกรรมการก่อนส่งร่างฉบับแก้ไข',
    general: 'ทั่วไป',
    articleLabel: 'มาตรา',
    draftDocument: 'เอกสารร่าง',
    uploadedDraftMeta: 'สัญญา Word ที่ผู้ใช้อัปโหลด',
    statusClarify: 'ต้องชี้แจง',
    statusResolved: 'แก้ไขแล้ว',
    notification: {
      commentTitle: 'ผู้ใช้เพิ่มความคิดเห็นใหม่ในงานรีวิว',
      commentMessage: (title) => `${title || 'มีข้อตกลงหนึ่งฉบับ'} มีการตอบกลับใหม่จากผู้ใช้ในบทสนทนารีวิวแบบสด`,
      reviewSource: 'รีวิวของคณะกรรมการ',
      openReview: 'เปิดรีวิว',
      commentNextStep: 'ดำเนินการสนทนาของคณะกรรมการต่อและตรวจสอบความคิดเห็นใหม่จากผู้ใช้',
      resubmitTitle: 'ผู้ใช้ส่งร่างฉบับแก้ไขกลับไปยังคณะกรรมการ',
      resubmitMessage: (title) => `${title || 'มีข้อตกลงหนึ่งฉบับ'} ได้รับการแก้ไขและพร้อมสำหรับการรีวิวรอบถัดไปของคณะกรรมการ`,
      resubmitNextStep: 'ตรวจสอบร่างฉบับแก้ไขและดำเนินการรีวิวของคณะกรรมการต่อ',
      responseNote: 'ส่งร่างฉบับแก้ไขกลับไปยังคณะกรรมการเพื่อรีวิวอีกรอบแล้ว'
    }
  }
}

export default {
  name: 'UserStartReview',
  components: {
    AgreementDocumentHeader,
    AgreementSignaturePreview
  },
  data() {
    return {
      error: '',
      success: '',
      agreement: null,
      agreementLoading: false,
      form: {
        title: '',
        partner: '',
        country: [],
        partnerEmail: '',
        contactPerson: '',
        department: 'International Affairs',
        summary: '',
        draftSource: 'builder',
        customDocument: {
          title: '',
          content: ''
        },
        uploadedDraft: null,
        partnerLogo: null
      },
      editableSections: [],
      countrySearch: '',
      countryPickerOpen: false,
      draftDirty: false,
      focusedSectionKey: '',
      showUserComposer: false,
      replyTargetThreadId: '',
      replyTargetCommentId: '',
      commentForm: {
        articleKey: 'general',
        section: '',
        page: 1,
        content: ''
      }
    }
  },
  computed: {
    localeCode() {
      return this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'th'
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    agreementId() {
      return this.$route.params.id
    },
    canEditAgreement() {
      const stage = String(this.agreement && this.agreement.derivedStage || '').trim().toLowerCase()
      return !['final-review', 'approved'].includes(stage)
    },
    countryOptions() {
      const values = Array.isArray(COUNTRY_OPTIONS) ? [...COUNTRY_OPTIONS] : []
      const currentValues = this.normalizeCountrySelection(this.form.country)
      currentValues.forEach((current) => {
        if (current && !values.includes(current)) values.unshift(current)
      })
      return values
    },
    selectedCountryValues() {
      return this.normalizeCountrySelection(this.form.country)
    },
    filteredCountryOptions() {
      const selected = new Set(this.selectedCountryValues)
      const query = this.normalizeCountrySearch(this.countrySearch)
      const options = this.countryOptions.filter(country => !selected.has(country))
      const filtered = query
        ? options.filter(country => this.countryMatchesSearch(country, query))
        : options
      return filtered.slice(0, 12)
    },
    selectedCountryText() {
      return this.formatCountryList(this.form.country)
    },
    resolvedCountryText() {
      return this.selectedCountryText || this.formatCountryList(this.agreement && this.agreement.country)
    },
    reviewComments() {
      return this.agreement && this.agreement.workflow && Array.isArray(this.agreement.workflow.comments)
        ? this.agreement.workflow.comments
        : []
    },
    commentThreads() {
      const map = new Map()
      this.reviewComments.forEach((item) => {
        const threadId = item.threadId || item.commentId || item.id
        if (!map.has(threadId)) {
          map.set(threadId, { threadId, root: null, replies: [], status: item.status || 'open' })
        }
        const bucket = map.get(threadId)
        if (item.parentId) {
          bucket.replies.push(item)
        } else if (!bucket.root) {
          bucket.root = item
        }
        if (item.status) bucket.status = item.status
      })
      return Array.from(map.values()).filter(item => item.root)
    },
    versions() {
      return this.agreement && Array.isArray(this.agreement.versions) ? [...this.agreement.versions].reverse() : []
    },
    latestVersionNumber() {
      return this.versions.length ? this.versions[0].versionNumber : 1
    },
    actionableCommentThreads() {
      return this.commentThreads.filter(thread => String(thread.root && thread.root.role || '').toLowerCase() === 'committee')
    },
    adminRevisionThreads() {
      return this.commentThreads.filter(thread => String(thread.root && thread.root.role || '').toLowerCase() === 'admin')
    },
    isRevisionStage() {
      return String(this.agreement && this.agreement.derivedStage || '').trim().toLowerCase() === 'revision'
    },
    hasAdminRevisionFeedback() {
      const decisionNote = String(this.agreement && this.agreement.workflow && this.agreement.workflow.decisionNote || '').trim()
      return this.isRevisionStage && (this.adminRevisionThreads.length > 0 || !!decisionNote)
    },
    actionableThreadCount() {
      return this.actionableCommentThreads.length
    },
    resolvedThreadCount() {
      return this.actionableCommentThreads.filter(thread => thread.status === 'resolved').length
    },
    waitingReplyThreadCount() {
      return this.actionableCommentThreads.filter((thread) => {
        if (thread.status === 'resolved') return false
        const latest = this.latestThreadComment(thread)
        return String(latest && latest.role || '').toLowerCase() !== 'user'
      }).length
    },
    canSubmitRevision() {
      if (!this.canEditAgreement) return false
      if (this.actionableThreadCount > 0) return this.waitingReplyThreadCount === 0
      return this.hasAdminRevisionFeedback
    },
    submitWarning() {
      if (!this.canEditAgreement) return this.text.submitWarningLocked
      if (!this.actionableThreadCount) return this.text.submitWarningNoComments
      if (this.waitingReplyThreadCount > 0) return this.text.submitWarningReplyAll
      return ''
    },
    repliedThreadCount() {
      return this.actionableCommentThreads.filter((thread) => {
        if (thread.status === 'resolved') return false
        const latest = this.latestThreadComment(thread)
        return String(latest && latest.role || '').toLowerCase() === 'user'
      }).length
    },
    articleOptions() {
      const sectionOptions = this.displayDraftSections
        .map((section, index) => ({
          key: String(section && section.key ? section.key : `article-${index + 1}`),
          title: String(section && section.title ? section.title : `Article ${index + 1}`)
        }))
        .filter((item) => item.title)

      if (sectionOptions.length) return sectionOptions

      return [{ key: 'general', title: this.text.general }]
    },
    uploadedDraftUrl() {
      return this.form && this.form.uploadedDraft && this.form.uploadedDraft.url
        ? this.form.uploadedDraft.url
        : ''
    },
    uploadedDraftName() {
      if (this.form && this.form.uploadedDraft && this.form.uploadedDraft.name) {
        return this.form.uploadedDraft.name
      }
      return ''
    },
    uploadedDraftMeta() {
      if (!(this.form && this.form.uploadedDraft)) return this.text.uploadedDraftMeta
      return this.form.uploadedDraft.sizeLabel || this.form.uploadedDraft.type || this.text.uploadedDraftMeta
    },
    partnerLogoUrl() {
      return this.form && this.form.partnerLogo && this.form.partnerLogo.url ? this.form.partnerLogo.url : ''
    },
    partnerLogoName() {
      return this.form && this.form.partnerLogo && this.form.partnerLogo.name
        ? this.form.partnerLogo.name
        : this.text.noPartnerLogo
    },
    baseDraftSections() {
      const currentDrafts = Array.isArray(this.editableSections) ? this.editableSections : []
      let sections = []
      
      if (currentDrafts.length) {
        sections = currentDrafts
          .map((item, index) => ({
            key: String(item && item.key ? item.key : `article-${index + 1}`),
            title: String(item && item.title ? item.title : `${this.text.articleLabel} ${index + 1}`),
            content: String(item && item.content ? item.content : ''),
            isArticle: true,
            isCustomDocument: !!(item && item.isCustomDocument)
          }))
          .filter((item) => item.content)
      } else {
        const customDocument = this.form && this.form.customDocument ? this.form.customDocument : null
        if (customDocument && customDocument.content) {
          sections = [{
            key: 'custom-document',
            title: customDocument.title || this.text.draftDocument,
            content: customDocument.content,
            isArticle: true,
            isCustomDocument: true
          }]
        } else {
          const latestVersion = this.versions.length ? this.versions[0] : null
          const versionDrafts = Array.isArray(latestVersion && latestVersion.articleDrafts) ? latestVersion.articleDrafts : []
          if (versionDrafts.length) {
            sections = versionDrafts
              .map((item, index) => ({
                key: String(item && item.key ? item.key : `version-article-${index + 1}`),
                title: String(item && item.title ? item.title : `${this.text.articleLabel} ${index + 1}`),
                content: String(item && item.content ? item.content : ''),
                isArticle: true
              }))
              .filter((item) => item.content)
          } else {
            const versionDocument = latestVersion && latestVersion.customDocument ? latestVersion.customDocument : null
            if (versionDocument && versionDocument.content) {
              sections = [{
                key: 'version-custom-document',
                title: versionDocument.title || this.text.draftDocument,
                content: versionDocument.content,
                isArticle: true
              }]
            }
          }
        }
      }

      // Add selected clauses to the preview
      const embeddedClauseIds = new Set(
        sections
          .map((item) => parseClauseArticleId(item && item.key ? item.key : ''))
          .filter(Boolean)
      )
      const selectedClauses = Array.isArray(this.agreement && this.agreement.selectedClauses)
        ? this.agreement.selectedClauses
        : (Array.isArray(this.agreement && this.agreement.selectedClauseIds) ? this.agreement.selectedClauseIds : [])
      
      if (selectedClauses.length) {
        const clauseSections = selectedClauses
          .map((item, index) => ({
            clauseId: String(item && (item.id || item._id) ? item.id || item._id : ''),
            key: `clause-${item.id || item._id || index}`,
            title: String(item.title || `Clause ${index + 1}`),
            content: String(item.content || ''),
            isClause: true,
            category: item.category || 'General'
          }))
          .filter((item) => item.content && !embeddedClauseIds.has(item.clauseId))
        
        sections = sections.concat(clauseSections)
      }

      const normalizedSections = Array.isArray(sections)
        ? sections.filter((section) => section && section.title)
        : []
      const detailLines = []
      if (this.form.title || (this.agreement && this.agreement.title)) {
        detailLines.push(this.form.title || this.agreement.title)
      }
      if (this.agreement && this.agreement.activityType) detailLines.push(`${this.text.fields.type}: ${this.agreement.activityType}`)
      if (this.form.partner || (this.agreement && this.agreement.partner)) {
        detailLines.push(`${this.text.fields.partner}: ${this.form.partner || this.agreement.partner}`)
      }
      const countryText = this.resolvedCountryText
      if (countryText) {
        detailLines.push(`${this.text.fields.country}: ${countryText}`)
      }
      const metadataSection = detailLines.length
        ? {
            key: 'agreement-title',
            title: this.text.fields.title,
            content: detailLines.join('\n')
          }
        : null

      return metadataSection
        ? [metadataSection, ...normalizedSections.filter(section => section.key !== metadataSection.key)]
        : normalizedSections
    },
    displayDraftSections() {
      const sections = this.baseDraftSections
      if (!sections.length) return []

      const focusKey = String(this.focusedSectionKey || '').trim()
      if (!focusKey) return sections

      const focusIndex = sections.findIndex(section => String(section.key) === focusKey)
      if (focusIndex <= 0) return sections

      const focusedSection = sections[focusIndex]
      return [focusedSection, ...sections.slice(0, focusIndex), ...sections.slice(focusIndex + 1)]
    },
    displayEditableSections() {
      const sections = Array.isArray(this.editableSections) ? this.editableSections : []
      if (!sections.length) return []

      const focusKey = String(this.focusedSectionKey || '').trim()
      if (!focusKey) return sections

      const focusIndex = sections.findIndex(section => String(section && section.key ? section.key : '') === focusKey)
      if (focusIndex <= 0) return sections

      const focusedSection = sections[focusIndex]
      return [focusedSection, ...sections.slice(0, focusIndex), ...sections.slice(focusIndex + 1)]
    }
  },
  mounted() {
    this.loadAgreement()
    this.refreshTimer = window.setInterval(() => {
      if (canAutoRefresh()) this.loadAgreement(true)
    }, 60000)
    window.addEventListener('focus', this.handleWindowFocus)
  },
  beforeDestroy() {
    if (this.refreshTimer) window.clearInterval(this.refreshTimer)
    window.removeEventListener('focus', this.handleWindowFocus)
  },
  methods: {
    openCountryPicker() {
      if (!this.canEditAgreement) return
      this.countryPickerOpen = true
    },
    handleCountryPickerBlur() {
      window.setTimeout(() => {
        const picker = this.$refs.countryPicker
        if (picker && picker.contains(document.activeElement)) return
        this.countryPickerOpen = false
      }, 0)
    },
    normalizeCountrySelection(value) {
      const rawValues = Array.isArray(value)
        ? value
        : String(value || '').split(',')
      return [...new Set(rawValues
        .map((item) => String(item || '').trim())
        .filter(Boolean))]
    },
    formatCountryList(value) {
      return this.normalizeCountrySelection(value).join(', ')
    },
    normalizeCountrySearch(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
    },
    countryMatchesSearch(country, query) {
      const normalizedCountry = this.normalizeCountrySearch(country)
      const countryTokens = normalizedCountry.split(/\s+/).filter(Boolean)
      const queryTokens = this.normalizeCountrySearch(query).split(/\s+/).filter(Boolean)
      if (!queryTokens.length) return true
      return queryTokens.every(token => (
        normalizedCountry.includes(token) ||
        countryTokens.some(countryToken => countryToken.startsWith(token))
      ))
    },
    addCountry(country) {
      if (!this.canEditAgreement) return
      const nextCountry = String(country || '').trim()
      if (!nextCountry) return
      const values = this.selectedCountryValues
      if (!values.includes(nextCountry)) {
        this.form.country = values.concat(nextCountry)
        this.onDraftFieldChange()
      }
      this.countrySearch = ''
      this.countryPickerOpen = true
    },
    removeCountry(country) {
      if (!this.canEditAgreement) return
      const nextCountry = String(country || '').trim()
      const nextValues = this.selectedCountryValues.filter(item => item !== nextCountry)
      if (nextValues.length === this.selectedCountryValues.length) return
      this.form.country = nextValues
      this.onDraftFieldChange()
    },
    selectFirstFilteredCountry() {
      if (!this.filteredCountryOptions.length) return
      this.addCountry(this.filteredCountryOptions[0])
    },
    removeLastCountryIfSearchEmpty(event) {
      if (String(this.countrySearch || '').length) return
      const values = this.selectedCountryValues
      if (!values.length) return
      event.preventDefault()
      this.form.country = values.slice(0, -1)
      this.onDraftFieldChange()
    },
    sectionDisplayTitle(section) {
      return getAgreementSectionDisplayTitle(section)
    },
    isSignaturePreviewSection(section) {
      return isSignatureAgreementSection(section)
    },
    buildEditableSectionsFromAgreement(sourceAgreement) {
      if (sourceAgreement && Array.isArray(sourceAgreement.articleDrafts) && sourceAgreement.articleDrafts.length) {
        return sourceAgreement.articleDrafts.map(item => ({ ...item, isCustomDocument: false }))
      }
      const customDocument = sourceAgreement && sourceAgreement.customDocument ? sourceAgreement.customDocument : null
      if (customDocument && (customDocument.title || customDocument.content)) {
        return [{
          key: 'custom-document',
          title: customDocument.title || this.text.draftDocument,
          content: customDocument.content || '',
          isCustomDocument: true
        }]
      }
      return []
    },
    applyAgreementToEditorState(sourceAgreement) {
      const nextAgreement = sourceAgreement || null
      this.form = {
        title: nextAgreement && nextAgreement.title ? nextAgreement.title : '',
        partner: nextAgreement && nextAgreement.partner ? nextAgreement.partner : '',
        country: this.normalizeCountrySelection(nextAgreement && nextAgreement.country ? nextAgreement.country : []),
        partnerEmail: nextAgreement && nextAgreement.partnerEmail ? nextAgreement.partnerEmail : '',
        contactPerson: nextAgreement && nextAgreement.contactPerson ? nextAgreement.contactPerson : '',
        department: nextAgreement && nextAgreement.department ? nextAgreement.department : 'International Affairs',
        summary: nextAgreement && nextAgreement.summary ? nextAgreement.summary : '',
        draftSource: nextAgreement && nextAgreement.draftSource ? nextAgreement.draftSource : 'builder',
        customDocument: nextAgreement && nextAgreement.customDocument ? { ...nextAgreement.customDocument } : { title: '', content: '' },
        uploadedDraft: nextAgreement && nextAgreement.uploadedDraft ? { ...nextAgreement.uploadedDraft } : null,
        partnerLogo: nextAgreement && nextAgreement.partnerLogo ? { ...nextAgreement.partnerLogo } : null
      }
      this.editableSections = this.buildEditableSectionsFromAgreement(nextAgreement)
    },
    buildDraftPayload() {
      const normalizedSections = Array.isArray(this.editableSections)
        ? this.editableSections.map((item, index) => ({
            key: item && item.key ? String(item.key) : `article-${index + 1}`,
            title: item && item.title ? String(item.title) : '',
            content: item && item.content ? String(item.content) : ''
          }))
        : []
      const usesCustomDocument = normalizedSections.length === 1
        && normalizedSections[0].key === 'custom-document'
        && !(this.agreement && Array.isArray(this.agreement.articleDrafts) && this.agreement.articleDrafts.length)
      const customDocument = usesCustomDocument
        ? {
            title: normalizedSections[0].title || '',
            content: normalizedSections[0].content || ''
          }
        : Object.assign({}, this.form.customDocument || {})

      return {
        title: this.form.title,
        partner: this.form.partner,
        country: this.selectedCountryText,
        partnerEmail: this.form.partnerEmail,
        contactPerson: this.form.contactPerson,
        department: this.form.department || 'International Affairs',
        summary: this.form.summary || '',
        draftSource: this.form.draftSource || 'builder',
        uploadedDraft: this.form.uploadedDraft || null,
        partnerLogo: this.form.partnerLogo || null,
        customDocument,
        articleDrafts: usesCustomDocument ? [] : normalizedSections
      }
    },
    onDraftFieldChange() {
      this.draftDirty = true
      this.error = ''
      this.success = ''
    },
    async loadAgreement(silent) {
      if (this.agreementLoading) return
      this.agreementLoading = true
      try {
        const response = await Service.agreements('detail', { id: this.agreementId })
        this.agreement = response && response.data && response.data.data ? response.data.data : null
        if (!silent || !this.draftDirty || !this.editableSections.length) {
          this.applyAgreementToEditorState(this.agreement)
          this.draftDirty = false
        }
        if (!silent && this.articleOptions.length) {
          this.commentForm.articleKey = this.articleOptions[0].key
          this.commentForm.section = this.articleOptions[0].title
        }
      } catch (error) {
        if (!silent) this.error = this.text.loadError || 'Unable to load this agreement.'
      } finally {
        this.agreementLoading = false
      }
    },
    handleWindowFocus() {
      if (canAutoRefresh()) this.loadAgreement(true)
    },
    applyArticleSelection() {
      const selected = this.articleOptions.find(item => item.key === this.commentForm.articleKey)
      if (selected) this.commentForm.section = selected.title
      if (selected) this.focusEditableSection(selected.key)
    },
    currentArticleTitle() {
      const selected = this.articleOptions.find(item => item.key === this.commentForm.articleKey)
      return selected ? selected.title : this.text.general
    },
    normalizeReviewTargetKey(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },
    isCommitteeComment(comment) {
      const role = String(comment && (comment.role || comment.roleLabel) || '').toLowerCase()
      return role.includes('committee')
    },
    isCommitteeThread(thread) {
      return !!(thread && thread.root && this.isCommitteeComment(thread.root))
    },
    commentTargetTitle(comment) {
      return String(comment && (comment.articleTitle || comment.section) ? (comment.articleTitle || comment.section) : this.text.general)
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    commentAnchorText(comment) {
      const anchor = comment && comment.anchor && typeof comment.anchor === 'object' ? comment.anchor : null
      return anchor && anchor.text ? String(anchor.text).trim() : ''
    },
    sectionHasAnchoredChanges(section) {
      return this.sectionChangeThreads(section).some(thread => this.commentAnchorText(thread.root))
    },
    buildSectionAnchorRanges(section) {
      const content = String(section && section.content ? section.content : '')
      if (!content) return []
      const ranges = []
      this.sectionChangeThreads(section).forEach((thread) => {
        const anchor = thread && thread.root && thread.root.anchor ? thread.root.anchor : null
        const text = anchor && anchor.text ? String(anchor.text).trim() : ''
        if (!text) return
        let start = Number(anchor.startOffset)
        let end = Number(anchor.endOffset)
        const offsetMatches = Number.isFinite(start) && Number.isFinite(end) && start >= 0 && end > start && content.slice(start, end).trim() === text
        if (!offsetMatches) {
          start = content.indexOf(text)
          end = start >= 0 ? start + text.length : -1
        }
        if (start < 0 || end <= start) return
        ranges.push({
          start,
          end,
          resolved: thread.status === 'resolved',
          label: thread.root.copy || thread.root.content || ''
        })
      })
      return ranges
        .sort((left, right) => left.start - right.start || right.end - left.end)
        .reduce((items, range) => {
          const previous = items[items.length - 1]
          if (previous && range.start < previous.end) return items
          items.push(range)
          return items
        }, [])
    },
    renderHighlightedSectionContent(section) {
      const content = String(section && section.content ? section.content : '')
      const ranges = this.buildSectionAnchorRanges(section)
      if (!ranges.length) return this.escapeHtml(content)
      let cursor = 0
      let html = ''
      ranges.forEach((range) => {
        html += this.escapeHtml(content.slice(cursor, range.start))
        const classes = ['comment-anchor-highlight']
        if (range.resolved) classes.push('comment-anchor-highlight--resolved')
        html += `<mark class="${classes.join(' ')}" title="${this.escapeHtml(range.label)}">${this.escapeHtml(content.slice(range.start, range.end))}</mark>`
        cursor = range.end
      })
      html += this.escapeHtml(content.slice(cursor))
      return html
    },
    threadMatchesSection(thread, section) {
      if (!(thread && thread.root && section)) return false
      const sectionKeys = [
        section.key,
        section.title
      ].map(value => this.normalizeReviewTargetKey(value)).filter(Boolean)
      const commentKeys = [
        thread.root.articleKey,
        thread.root.articleTitle,
        thread.root.section
      ].map(value => this.normalizeReviewTargetKey(value)).filter(Boolean)
      return commentKeys.some(key => sectionKeys.includes(key))
    },
    sectionChangeThreads(section) {
      return this.commentThreads.filter(thread => this.isCommitteeThread(thread) && this.threadMatchesSection(thread, section))
    },
    sectionOpenChangeCount(section) {
      return this.sectionChangeThreads(section).filter(thread => thread.status !== 'resolved').length
    },
    sectionChangeLabel(section) {
      const openCount = this.sectionOpenChangeCount(section)
      if (openCount > 0) return `${openCount} ${this.text.needsChange}`
      return this.text.changeResolved
    },
    editableSectionClass(section) {
      const sectionKey = String(section && section.key ? section.key : '')
      const changeThreads = this.sectionChangeThreads(section)
      const openCount = changeThreads.filter(thread => thread.status !== 'resolved').length
      return {
        'editable-section--focus': this.focusedSectionKey === sectionKey,
        'editable-section--has-change': changeThreads.length > 0,
        'editable-section--open-change': openCount > 0
      }
    },
    editableSectionId(section) {
      const key = section && section.key ? section.key : 'section'
      return `editable-section-${String(key).replace(/[^a-zA-Z0-9_-]/g, '-')}`
    },
    focusEditableSection(articleKey) {
      if (!articleKey) return
      this.focusedSectionKey = articleKey
      this.$nextTick(() => {
        const target = document.getElementById(`editable-section-${String(articleKey).replace(/[^a-zA-Z0-9_-]/g, '-')}`)
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    openThreadContext(thread) {
      if (!(thread && thread.root)) return
      if (thread.root.articleKey) {
        this.focusEditableSection(thread.root.articleKey)
        return
      }
      const matchingSection = this.displayEditableSections.find(section => this.threadMatchesSection(thread, section))
      if (matchingSection && matchingSection.key) this.focusEditableSection(matchingSection.key)
    },
    openUserComposer() {
      if (!this.canEditAgreement) return
      this.showUserComposer = true
      this.clearReplyTarget()
      const firstArticle = this.articleOptions.length ? this.articleOptions[0] : null
      if (firstArticle) {
        this.commentForm.articleKey = firstArticle.key
        this.commentForm.section = firstArticle.title
      }
      this.$nextTick(() => {
        const target = document.getElementById('user-comment-box')
        if (target) target.focus()
      })
    },
    closeUserComposer() {
      this.showUserComposer = false
      this.clearReplyTarget()
      this.commentForm.content = ''
    },
    resolveCommentIdentifier(comment) {
      if (!(comment && typeof comment === 'object')) return ''
      return comment.commentId || comment.id || ''
    },
    prepareReply(thread, comment = null) {
      if (!this.canEditAgreement) return
      if (!(thread && thread.root)) return
      const targetComment = comment && typeof comment === 'object' ? comment : thread.root
      const parentCommentId = this.resolveCommentIdentifier(targetComment)
      if (!parentCommentId) return
      this.showUserComposer = true
      this.commentForm.content = ''
      this.replyTargetThreadId = thread.threadId
      this.replyTargetCommentId = parentCommentId
      this.commentForm.articleKey = targetComment.articleKey || thread.root.articleKey || 'general'
      this.commentForm.section = targetComment.section || targetComment.articleTitle || thread.root.section || thread.root.articleTitle || this.text.general
      this.commentForm.page = targetComment.page || thread.root.page || 1
      this.focusEditableSection(targetComment.articleKey || thread.root.articleKey || '')
      this.$nextTick(() => {
        const target = document.getElementById('user-comment-box')
        if (target) target.focus()
      })
    },
    clearReplyTarget() {
      this.replyTargetThreadId = ''
      this.replyTargetCommentId = ''
    },
    latestThreadComment(thread) {
      if (!(thread && thread.root)) return null
      return Array.isArray(thread.replies) && thread.replies.length
        ? thread.replies[thread.replies.length - 1]
        : thread.root
    },
    threadStatusLabel(status) {
      return {
        clarification: this.text.statusClarify,
        resolved: this.text.statusResolved
      }[String(status || 'open')] || ''
    },
    shouldShowThreadStatusBadge(status) {
      return ['clarification', 'resolved'].includes(String(status || 'open'))
    },
    threadStatusClass(status) {
      return `thread-state-pill--${String(status || 'open')}`
    },
    formatCommentTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    handlePartnerLogoUpload(event) {
      if (!this.canEditAgreement) return
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const allowedLogoTypes = ['image/png', 'image/jpeg']
      const fallbackName = String(file.name || '').toLowerCase()
      const isAllowedLogo = allowedLogoTypes.includes(file.type) || /\.(png|jpe?g)$/.test(fallbackName)
      if (!isAllowedLogo) {
        this.error = this.text.logoFileTypeError
        event.target.value = ''
        return
      }
      this.error = ''

      const reader = new FileReader()
      reader.onload = () => {
        this.form = Object.assign({}, this.form, {
          partnerLogo: {
            name: file.name,
            type: file.type || 'image/png',
            url: typeof reader.result === 'string' ? reader.result : ''
          }
        })
        this.onDraftFieldChange()
      }
      reader.readAsDataURL(file)
    },
    async saveDraftChanges() {
      if (!this.canEditAgreement || !this.agreement) return
      const response = await Service.agreements('update-draft', {
        id: this.agreementId,
        ...this.buildDraftPayload()
      })
      this.agreement = response && response.data && response.data.data ? response.data.data : this.agreement
      this.applyAgreementToEditorState(this.agreement)
      this.draftDirty = false
      this.error = ''
      this.success = this.text.saveSuccess
    },
    async saveResponseComment() {
      if (!this.canEditAgreement) return
      const content = String(this.commentForm.content || '').trim()
      if (!content) return
      const article = this.articleOptions.find(item => item.key === this.commentForm.articleKey) || { title: this.text.general }
      const response = await Service.agreements('comment', {
        id: this.agreementId,
        content,
        articleKey: this.commentForm.articleKey,
        articleTitle: article.title,
        page: this.commentForm.page,
        section: this.commentForm.section,
        threadId: this.replyTargetThreadId,
        parentId: this.replyTargetCommentId
      })
      this.agreement = response && response.data && response.data.data ? response.data.data : this.agreement
      createWorkflowNotification({
        role: 'committee',
        type: 'review',
        title: this.text.notification.commentTitle,
        message: this.text.notification.commentMessage(this.agreement && this.agreement.title),
        source: this.text.notification.reviewSource,
        reference: this.agreement ? `MOU-${this.agreement.id}` : '',
        priority: 'medium',
        targetPath: this.agreement ? `/review?agreementId=${this.agreement.id}` : '/dashboard/committee',
        actionLabel: this.text.notification.openReview,
        nextStep: this.text.notification.commentNextStep
      })
      this.commentForm.content = ''
      this.commentForm.page = 1
      this.commentForm.articleKey = this.articleOptions.length ? this.articleOptions[0].key : 'general'
      this.commentForm.section = this.articleOptions.length ? this.articleOptions[0].title : this.text.general
      this.showUserComposer = false
      this.clearReplyTarget()
    },
    async resubmit() {
      if (!this.canSubmitRevision) return
      const response = await Service.agreements('resubmit', {
        id: this.agreementId,
        responseNote: this.text.notification.responseNote,
        ...this.buildDraftPayload()
      })
      const saved = response && response.data && response.data.data ? response.data.data : this.agreement
      this.draftDirty = false
      createWorkflowNotification({
        role: 'committee',
        type: 'review',
        title: this.text.notification.resubmitTitle,
        message: this.text.notification.resubmitMessage(saved && saved.title),
        source: this.text.notification.reviewSource,
        reference: saved ? `MOU-${saved.id}` : '',
        priority: 'high',
        targetPath: saved ? `/review?agreementId=${saved.id}` : '/dashboard/committee',
        actionLabel: this.text.notification.openReview,
        nextStep: this.text.notification.resubmitNextStep
      })
      this.$router.push('/dashboard/user-review-queue')
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("user-start-review");

.shell { border-radius: var(--app-radius-xl); }
.kicker { color: #8c1515; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.page-header h2 { color: #344767; margin: 6px 0; }
.page-header p, .muted, .file-meta { color: #718096; }
.notice { border-radius: 14px; font-weight: 600; margin-top: 16px; padding: 12px 14px; }
.notice--error { background: #fff5f5; border: 1px solid #fecaca; color: #b91c1c; }
.notice--success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }
.panel-card__document-header { margin: 16px 0 18px; }
.detail-card, .panel-card { background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%); border: 1px solid var(--app-border); border-radius: var(--app-radius-lg); box-shadow: var(--app-shadow-soft); padding: 16px; }
.panel-card--compact { padding: 14px 16px; }
.panel-card__header { align-items: flex-start; display: flex; gap: 12px; justify-content: space-between; }
.panel-note { color: #718096; margin-top: 8px; }
.detail-row { align-items: center; display: flex; justify-content: space-between; padding: 6px 0; }
.detail-row span { color: #718096; }
.panel-card--document-preview { margin-top: 20px; }
.review-layout { display: grid; gap: 16px; grid-template-columns: minmax(0, 1.25fr) minmax(360px, 0.9fr); margin-top: 20px; }
.review-main, .review-side { display: flex; flex-direction: column; gap: 16px; }
.edit-state-pill { background: #ecfdf5; border-radius: 999px; color: #15803d; display: inline-flex; font-size: 0.8rem; font-weight: 700; padding: 8px 12px; }
.edit-state-pill--locked { background: #eff6ff; color: #1d4ed8; }
.lock-banner { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 14px; color: #9a3412; font-weight: 600; margin-top: 14px; padding: 12px 14px; }
.meta-form-grid { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: 16px; }
.comment-card { background: #fbfcfe; border: 1px solid #e6ebf1; border-radius: 14px; margin-top: 12px; padding: 12px; }
.comment-card--interactive { cursor: pointer; transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease; }
.comment-card--interactive:hover, .comment-card--interactive:focus { border-color: #d5deea; box-shadow: 0 10px 24px rgba(44, 52, 71, 0.08); outline: none; transform: translateY(-1px); }
.comment-card--active { border: 1px solid #f5c27a; box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.14); }
.comment-card--unresolved { background: #fff5f5; border-color: #fecaca; }
.comment-card--resolved { background: #f0fdf4; border-color: #86efac; }
.comment-thread { margin-top: 12px; }
.thread-replies { border-left: 2px solid #e6ebf1; margin-left: 18px; padding-left: 14px; }
.comment-card--reply { background: #fff; }
.comment-head { align-items: center; display: flex; justify-content: space-between; gap: 10px; }
.comment-meta { color: #718096; font-size: 0.9rem; margin-top: 6px; }
.comment-change-target { align-items: flex-start; background: #fff7ed; border: 1px solid #fed7aa; border-radius: 10px; color: #7c2d12; display: grid; gap: 2px; margin-top: 10px; padding: 9px 11px; }
.comment-change-target span { font-size: 0.74rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; }
.comment-change-target strong { color: #344767; }
.comment-change-target em { background: rgba(255, 255, 255, 0.76); border-left: 3px solid currentColor; border-radius: 6px; display: block; font-style: normal; font-weight: 800; margin-top: 5px; padding: 6px 8px; }
.comment-change-target--resolved { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
.thread-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 8px; }
.thread-link { background: none; border: 0; color: #9e212f; cursor: pointer; font-weight: 700; padding: 0; }
.thread-link:hover, .thread-link:focus { color: #8a1728; outline: none; text-decoration: underline; }
.thread-state-pill { align-items: center; border-radius: 999px; display: inline-flex; font-size: 0.78rem; font-weight: 700; margin-top: 10px; padding: 4px 10px; }
.thread-state-pill--open { background: #fff7ed; color: #9a3412; }
.thread-state-pill--addressed { background: #f7e8eb; color: #8a1728; }
.thread-state-pill--clarification { background: #fef3c7; color: #92400e; }
.thread-state-pill--resolved { background: #ecfdf5; color: #15803d; }
.inline-reply-indicator { align-items: center; color: #9e212f; display: flex; gap: 8px; font-size: 0.92rem; font-weight: 700; margin: 8px 4px 0; }
.inline-reply-indicator__arrow { font-size: 1rem; line-height: 1; }
.composer-launch { margin-top: 14px; }
.composer-launch__btn { background: linear-gradient(135deg, #9e212f 0%, #8a1728 100%); border: 1px solid #9e212f; border-radius: 999px; color: #fff; cursor: pointer; font-weight: 700; padding: 10px 16px; transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease; }
.composer-launch__btn:hover, .composer-launch__btn:focus { background: linear-gradient(135deg, #8a1728 0%, #721220 100%); border-color: #8a1728; box-shadow: 0 10px 24px rgba(158, 33, 47, 0.2); outline: none; transform: translateY(-1px); }
.form-stack--composer { background: #fffaf5; border: 1px solid #fed7aa; border-radius: 16px; margin-top: 16px; padding: 16px; }
.form-stack--inline { margin-top: 10px; }
.panel-title--composer { margin-top: 8px; }
.decision-label { color: #4a5568; display: block; font-weight: 600; margin-top: 4px; margin-bottom: 8px; }
.decision-select { border: 1px solid #d9e2ec; border-radius: 14px; margin-top: 8px; padding: 12px 14px; width: 100%; }
.decision-note { border: 1px solid #d9e2ec; border-radius: 14px; padding: 14px; resize: vertical; width: 100%; }
.decision-actions { display: flex; gap: 12px; margin-top: 18px; }
.article-reference { background: #fff; border: 1px solid #fde7c2; border-radius: 12px; color: #9a3412; font-size: 0.92rem; font-weight: 600; padding: 10px 12px; }
.field { align-content: start; display: grid; gap: 8px; margin-top: 14px; }
.field--full { grid-column: 1 / -1; }
.field-heading {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}
.field-heading--compact {
  align-items: flex-start;
  flex-wrap: wrap;
}
.field span { color: #344767; font-weight: 700; }
.field > input,
.field > select {
  border: 1px solid var(--app-border-strong);
  border-radius: var(--app-radius-md);
  box-sizing: border-box;
  height: 48px;
  padding: 0 12px;
  width: 100%;
}
.field > textarea {
  border: 1px solid var(--app-border-strong);
  border-radius: var(--app-radius-md);
  box-sizing: border-box;
  min-height: 112px;
  padding: 12px;
  resize: vertical;
  width: 100%;
}
.field-help {
  color: #718096;
  font-size: 0.78rem;
  line-height: 1.4;
}
.country-picker {
  position: relative;
}
.country-picker__control {
  align-items: center;
  background: #fff;
  border: 1px solid var(--app-border-strong);
  border-radius: var(--app-radius-md);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 48px;
  padding: 8px;
}
.country-picker__input {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  flex: 1 1 140px;
  min-width: 120px;
  outline: none;
  padding: 4px !important;
}
.country-picker__input::-webkit-search-decoration,
.country-picker__input::-webkit-search-cancel-button,
.country-picker__input::-webkit-search-results-button,
.country-picker__input::-webkit-search-results-decoration {
  display: none;
}
.field .country-picker__placeholder {
  color: #8a97a8;
  font-weight: 500;
}
.country-picker__menu {
  background: #fff;
  border: 1px solid #dfe7f0;
  border-radius: 14px;
  box-shadow: 0 18px 36px rgba(44, 52, 71, 0.14);
  display: grid;
  gap: 4px;
  left: 0;
  margin-top: 6px;
  max-height: 260px;
  overflow: auto;
  padding: 8px;
  position: absolute;
  right: 0;
  z-index: 20;
}
.country-picker__option {
  background: transparent;
  border: 0;
  border-radius: 10px;
  color: #344767;
  cursor: pointer;
  font-weight: 700;
  padding: 10px;
  text-align: left;
}
.country-picker__option:hover,
.country-picker__option:focus {
  background: #fff6ea;
  color: #8c1515;
  outline: none;
}
.country-picker__empty {
  color: #718096;
  padding: 10px;
}
.country-picker--disabled .country-picker__control {
  background: #f8fafc;
}
.field .country-chip {
  align-items: center;
  background: #fff3cd;
  border: 1px solid #f0d98c;
  border-radius: 999px;
  color: #745b13;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 6px;
  line-height: 1;
  min-height: 30px;
  padding: 6px 8px 6px 10px;
}
.country-chip__remove {
  align-items: center;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(116, 91, 19, 0.18);
  border-radius: 999px;
  color: #8c1515;
  cursor: pointer;
  display: inline-flex;
  height: 20px;
  justify-content: center;
  padding: 0;
  width: 20px;
}
.asset-card { align-items: center; background: #fbfcfe; border: 1px solid #e6ebf1; border-radius: 14px; display: flex; gap: 12px; min-height: 72px; padding: 12px; }
.asset-card__preview { align-items: center; background: #fff; border: 1px solid #e6ebf1; border-radius: 12px; display: flex; height: 56px; justify-content: center; overflow: hidden; width: 56px; }
.asset-card__image { height: 100%; object-fit: cover; width: 100%; }
.panel-card--composer { background: #fffaf5; border-color: #fed7aa; }
.panel-card--inline { margin-top: 10px; }
.editable-sections { display: grid; gap: 14px; }
.editable-section { background: #fbfcfe; border: 1px solid #e6ebf1; border-radius: 14px; padding: 14px; scroll-margin-top: 110px; transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease; }
.editable-section--focus { border-color: #f59e0b; box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.22), 0 18px 32px rgba(146, 64, 14, 0.12); }
.editable-section--has-change { background: #fffaf0; border-color: #f8c15c; }
.editable-section--open-change { background: #fff7ed; border-color: #f97316; box-shadow: inset 5px 0 0 #f97316; }
.editable-section--focus.editable-section--open-change { box-shadow: inset 5px 0 0 #f97316, 0 0 0 4px rgba(245, 158, 11, 0.22), 0 18px 32px rgba(146, 64, 14, 0.12); }
.editable-section__anchored-preview { background: #fff; border: 1px dashed #fbbf24; border-radius: 10px; color: #52606d; line-height: 1.7; margin-bottom: 10px; padding: 10px 12px; white-space: pre-wrap; }
.section-change-callout { background: #fff; border: 1px solid #fed7aa; border-radius: 12px; display: grid; gap: 8px; margin-bottom: 14px; padding: 12px; }
.section-change-callout__head { display: grid; gap: 3px; }
.section-change-callout__head span { color: #9e212f; font-size: 0.74rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; }
.section-change-callout__head strong { color: #344767; }
.section-change-callout__item { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 10px; color: #7c2d12; cursor: pointer; font-weight: 700; overflow: hidden; padding: 9px 11px; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.section-change-callout__item:hover, .section-change-callout__item:focus { border-color: #f97316; box-shadow: 0 8px 18px rgba(249, 115, 22, 0.12); outline: none; }
.section-change-callout__item--resolved { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
.document-file-card { align-items: center; background: #fbfcfe; border: 1px solid #e6ebf1; border-radius: 14px; display: flex; justify-content: space-between; gap: 12px; margin-top: 14px; padding: 14px; }
.document-file-card strong { color: #344767; display: block; }
.document-file-card__link { color: #8c1515; font-weight: 700; white-space: nowrap; }
.document-preview { margin-top: 18px; }
.preview-section { background: #fbfcfe; border: 1px solid #e6ebf1; border-radius: 14px; padding: 16px; }
.preview-section + .preview-section { margin-top: 14px; }
.preview-section--focus { border-color: #f5c27a; box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.12); }
.preview-section--clause { background: #fffaf2; border-color: #dfbf8a; }
.preview-section__label { color: #344767; font-size: 0.82rem; font-weight: 700; margin-bottom: 8px; text-transform: uppercase; display: flex; gap: 8px; align-items: center; }
.clause-badge { background: #fff4df; border-radius: 6px; color: #8c1515; font-size: 0.7rem; font-weight: 600; padding: 4px 8px; text-transform: none; }
.preview-section p { color: #52606d; line-height: 1.7; margin: 0; white-space: pre-wrap; }
.comment-anchor-highlight { background: #fde68a; border-radius: 4px; box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.18); color: #713f12; font-weight: 800; padding: 1px 3px; }
.comment-anchor-highlight--resolved { background: #bbf7d0; box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.16); color: #14532d; }
.version-list { display: grid; gap: 10px; margin-top: 12px; }
.version-list--compact { gap: 8px; margin-top: 10px; }
.version-item { align-items: center; background: #fbfcfe; border-radius: 12px; display: grid; gap: 4px; padding: 12px; }
.version-item--compact { gap: 2px; padding: 10px 12px; }
.version-item--compact span { font-size: 0.9rem; }
.submit-summary { color: #718096; display: flex; flex-wrap: wrap; gap: 14px; margin-top: 12px; }
.submit-warning { color: #9a3412; font-size: 0.92rem; font-weight: 600; margin-top: 12px; }
.draft-state { color: #718096; font-size: 0.92rem; font-weight: 600; }
.actions { display: flex; justify-content: flex-end; margin-top: 18px; }
.actions--between { align-items: center; justify-content: space-between; }

@media (max-width: 768px) {
  .meta-form-grid { grid-template-columns: 1fr; }
  .review-layout { grid-template-columns: 1fr; }
  .actions, .decision-actions { justify-content: flex-start; }
  .actions--between { align-items: flex-start; flex-direction: column; gap: 12px; }
  .panel-card__header { flex-direction: column; }
}
</style>
